import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { 
  User, Shield, AlertTriangle, FileSearch, Server, UserCheck,
  MessageSquare, Scale, ChevronDown, ChevronUp, FileText, CheckCircle
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import API from "../lib/api";
import { toast } from "sonner";

const AGENT_ICONS = {
  lead_auditor: UserCheck,
  skeptical_watchdog: AlertTriangle,
  detail_perfectionist: FileSearch,
  compliance_guardian: Shield,
  it_specialist: Server
};

export function DebatePanel({ debates = [], auditRunId, onUpdate }) {
  const [expandedDebates, setExpandedDebates] = useState({});
  const [resolving, setResolving] = useState(null);

  const toggleDebate = (id) => {
    setExpandedDebates(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleResolve = async (debateId, decision) => {
    setResolving(debateId);
    try {
      await API.put(`/audit-runs/${auditRunId}/team/debate/${debateId}/resolve`, {
        user_decision: decision,
        resolution: `User selected: ${decision}`
      });
      toast.success("Debate resolved");
      onUpdate?.();
    } catch (err) {
      toast.error("Failed to resolve debate");
    } finally {
      setResolving(null);
    }
  };

  const openDebates = debates.filter(d => d.status === "open");
  const resolvedDebates = debates.filter(d => d.status !== "open");

  if (debates.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="debate-panel">
      {openDebates.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-medium">Disagreements Requiring Input ({openDebates.length})</h3>
          </div>

          {openDebates.map((debate) => {
            const isExpanded = expandedDebates[debate.id];

            return (
              <Card key={debate.id} className="border-amber-500/50 bg-amber-500/5">
                <Collapsible open={isExpanded} onOpenChange={() => toggleDebate(debate.id)}>
                  <CardHeader className="py-3 cursor-pointer" onClick={() => toggleDebate(debate.id)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-amber-500" />
                        <CardTitle className="text-sm">{debate.topic}</CardTitle>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs border-amber-500 text-amber-500">
                          {debate.positions?.length || 0} positions
                        </Badge>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                  </CardHeader>

                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-3 space-y-4">
                      {/* Positions */}
                      <div className="space-y-3">
                        {debate.positions?.map((pos, i) => {
                          const IconComponent = AGENT_ICONS[pos.agent_id] || User;
                          return (
                            <div key={i} className="p-3 rounded-lg bg-background border">
                              <div className="flex items-center gap-2 mb-2">
                                <IconComponent className="w-4 h-4" />
                                <span className="text-sm font-medium">{pos.agent_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {Math.round(pos.confidence * 100)}% confident
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{pos.position}</p>
                              {pos.evidence_cited?.length > 0 && (
                                <div className="flex gap-1 mt-2 flex-wrap">
                                  {pos.evidence_cited.map((ev, j) => (
                                    <Badge key={j} variant="secondary" className="text-xs">
                                      <FileText className="w-3 h-3 mr-1" /> {ev}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Lead Auditor's Proposed Resolution */}
                      {debate.lead_resolution && (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <div className="flex items-center gap-2 mb-1">
                            <UserCheck className="w-4 h-4 text-emerald-500" />
                            <span className="text-sm font-medium">Lead Auditor's Recommendation</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{debate.lead_resolution}</p>
                        </div>
                      )}
                    </CardContent>

                    <CardFooter className="py-3 border-t flex gap-2 flex-wrap">
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolve(debate.id, "request_more_evidence")}
                        disabled={resolving === debate.id}
                      >
                        Request More Evidence
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolve(debate.id, "conservative_position")}
                        disabled={resolving === debate.id}
                      >
                        Proceed Conservative
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => handleResolve(debate.id, "practical_position")}
                        disabled={resolving === debate.id}
                      >
                        Proceed Practical
                      </Button>
                    </CardFooter>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}

      {/* Resolved Debates */}
      {resolvedDebates.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Resolved ({resolvedDebates.length})
          </h4>
          {resolvedDebates.map((debate) => (
            <div 
              key={debate.id} 
              className="flex items-center justify-between p-2 rounded border bg-muted/20"
            >
              <span className="text-xs">{debate.topic}</span>
              <Badge variant="outline" className="text-xs">
                {debate.user_decision?.replace(/_/g, " ")}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DebatePanel;
